import React, { useRef, useState } from 'react';
import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

export interface ActionMenuItem {
  key: string;
  label: string;
  /** A second line, for choices that need a word of explanation. */
  description?: string;
  icon?: React.ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  /**
   * `danger` for an action that destroys something — Delete. It is drawn in
   * red, icon and word, below a line, so it is never picked by a slip.
   */
  tone?: 'danger';
  /**
   * Choices of its own — "Download" with its formats. The item then opens a
   * second menu beside the first rather than doing anything itself, and says
   * so with a chevron. Two levels is the limit: a menu you have to navigate
   * is no longer a shortlist.
   */
  items?: ActionMenuItem[];
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Text on the button, e.g. "Download". Leave out for an icon-only button. */
  label?: string;
  icon?: React.ReactNode;
  /** Names an icon-only button, and the tooltip on it. */
  'aria-label'?: string;
  disabled?: boolean;
}

/**
 * A button that opens a short list of actions — Download ▾ with its formats,
 * or ⋯ for the less common things. Each item runs its action and closes the
 * menu.
 *
 * An item carrying `items` opens a second menu beside the first, marked with
 * a chevron: for a choice that needs a choice, like which format to download.
 * Picking from it closes both, so one decision means one round trip.
 *
 * For choosing a value that stays chosen, use `SelectMenu` instead.
 */
export function ActionMenu({ items, label, icon, 'aria-label': ariaLabel, disabled }: ActionMenuProps) {
  const anchor = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState<{ key: string; anchor: HTMLElement } | null>(null);
  const name = ariaLabel ?? label ?? 'More actions';

  const closeAll = () => {
    setSub(null);
    setOpen(false);
  };

  const trigger = label ? (
    <Button
      ref={anchor}
      size="small"
      color="inherit"
      variant="outlined"
      disabled={disabled}
      onClick={() => setOpen(true)}
      aria-haspopup="menu"
      aria-expanded={open}
      startIcon={icon}
      endIcon={<ExpandMoreRounded fontSize="small" />}
      sx={(t) => ({
        minHeight: t.ds.sizes.control.sm,
        whiteSpace: 'nowrap',
        color: t.tokens.text.primary,
        bgcolor: t.tokens.surface.raised,
        borderColor: t.tokens.border.default,
        '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: t.tokens.text.secondary },
        '&:hover': { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.raised },
      })}
    >
      {label}
    </Button>
  ) : (
    <Tooltip title={name}>
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          ref={anchor}
          size="small"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-label={name}
          aria-haspopup="menu"
          aria-expanded={open}
          // The same square, bordered look as an icon-only ToolbarButton.
          sx={(t) => ({
            width: t.ds.sizes.control.sm,
            height: t.ds.sizes.control.sm,
            borderRadius: `${t.ds.radius.sm}px`,
            border: `1px solid ${t.tokens.border.default}`,
            bgcolor: t.tokens.surface.raised,
            color: t.tokens.text.secondary,
            '&:hover': { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.raised, color: t.tokens.text.primary },
          })}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  // Either every row has the icon column or none does, so the labels line up
  // whether or not a particular action has an icon of its own.
  const iconColumn = (icon: React.ReactNode, show: boolean) =>
    show ? <ListItemIcon sx={{ minWidth: 30 }}>{icon ?? null}</ListItemIcon> : null;

  const anyIcon = items.some((i) => i.icon);

  const renderItem = (item: ActionMenuItem, index: number, all: ActionMenuItem[]) => {
    const hasChildren = Boolean(item.items?.length);
    const danger = item.tone === 'danger';
    // A destructive item sits apart from the rest, below a line.
    const divided = danger && index > 0 && all[index - 1].tone !== 'danger';
    return (
      <MenuItem
        key={item.key}
        disabled={item.disabled}
        sx={(t) => ({
          ...(divided && { borderTop: `1px solid ${t.tokens.border.subtle}`, mt: 0.5, pt: 1 }),
          ...(danger && {
            color: t.tokens.semantic.danger,
            '& .MuiListItemIcon-root': { color: t.tokens.semantic.danger },
          }),
        })}
        aria-haspopup={hasChildren ? 'menu' : undefined}
        aria-expanded={hasChildren ? sub?.key === item.key : undefined}
        onClick={(e) => {
          if (hasChildren) {
            setSub({ key: item.key, anchor: e.currentTarget });
            return;
          }
          closeAll();
          item.onSelect?.();
        }}
      >
        {iconColumn(item.icon, anyIcon)}
        <ListItemText
          primary={item.label}
          secondary={item.description}
          slotProps={{
            primary: { variant: 'body2' },
            secondary: { variant: 'caption' },
          }}
        />
        {hasChildren && (
          <ChevronRightRounded
            fontSize="small"
            aria-hidden
            sx={(t) => ({ ml: 1.5, color: t.tokens.text.tertiary })}
          />
        )}
      </MenuItem>
    );
  };

  const openSub = items.find((i) => i.key === sub?.key);

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchor.current}
        open={open}
        onClose={closeAll}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items.map(renderItem)}
      </Menu>

      {openSub && (
        <Menu
          anchorEl={sub?.anchor ?? null}
          open={Boolean(sub)}
          onClose={() => setSub(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{ dense: true, 'aria-label': openSub.label }}
        >
          {(openSub.items ?? []).map((child) => (
            <MenuItem
              key={child.key}
              disabled={child.disabled}
              onClick={() => {
                closeAll();
                child.onSelect?.();
              }}
            >
              {iconColumn(child.icon, (openSub.items ?? []).some((c) => c.icon))}
              <ListItemText
                primary={child.label}
                secondary={child.description}
                slotProps={{
                  primary: { variant: 'body2' },
                  secondary: { variant: 'caption' },
                }}
              />
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
